export type ProgressStatus = "idle" | "progressing" | "done" | "paused";

export type Progress = {
  status: ProgressStatus;
  progress: number;
  progressId: string;
} & AdditionalProgressInfo;

export type AdditionalProgressInfo = {
  previousProgress: number;
  rawProgress: number;
  rawPreviousProgress: number;
  progressWhenDone: number;
  pauseReason?: string;
};

export type ProgressListener = (progress: Progress) => void;

export abstract class ProgressMixin {
  protected _progressStatus: ProgressStatus = "idle";
  protected _previousProgress: number | undefined;
  protected _progress: number | undefined;
  protected _progressWhenDone: number | undefined;
  protected _progressListeners: Map<string, ProgressListener> | undefined;
  protected _pauseReason: string | undefined;

  setProgress(progress: number) {
    this._previousProgress = this._progress;
    this._progress = Math.min(progress, this._progressWhenDone ?? 0);
    if (this._progress === this._progressWhenDone) {
      this._progressStatus = "done";
    } else if (this._previousProgress === 0 && this._progress > 0) {
      this._progressStatus = "progressing";
    }
    this.notifyProgress();
  }

  pauseProgress(pauseReason?: string) {
    if (this._progressStatus === "progressing") {
      this._progressStatus = "paused";
      this._pauseReason = pauseReason;
      this.notifyProgress();
    }
  }

  resumeProgress() {
    if (this._progressStatus === "paused") {
      this._progressStatus = "progressing";
      this._pauseReason = undefined;
      this.notifyProgress();
    }
  }

  incrementProgress() {
    if (this._progress === undefined) {
      this._progress = 0;
    }
    this.setProgress(this._progress + 1);
  }

  getProgress(progress: number | undefined): number {
    if (!this._progressWhenDone || !progress) {
      return 0;
    }
    return (progress / this._progressWhenDone) * 100;
  }

  setProgressWhenDone(progressWhenDone: number) {
    this._progressWhenDone = progressWhenDone;
  }

  notifyProgress() {
    if (!this._progressListeners) {
      this._progressListeners = new Map();
    }
    const progressInfo = {
      status: this._progressStatus,
      progress: this.getProgress(this._progress),
      previousProgress: this.getProgress(this._previousProgress),
      rawProgress: this._progress ?? 0,
      rawPreviousProgress: this._previousProgress ?? 0,
      progressWhenDone: this._progressWhenDone ?? 0,
      pauseReason: this._pauseReason,
    };
    const entries = this._progressListeners.entries();
    for (const [progressId, listener] of entries) {
      listener({ ...progressInfo, progressId });
    }
  }

  unsubscribeFromProgress(id: string) {
    if (!this._progressListeners) {
      this._progressListeners = new Map();
    }
    this._progressListeners.delete(id);
  }

  subscribeToProgress(id: string, listener: ProgressListener): () => void {
    if (!this._progressListeners) {
      this._progressListeners = new Map();
    }
    this._progressListeners.set(id, listener);
    return () => {
      this.unsubscribeFromProgress(id);
    };
  }

  initProgress() {
    this._previousProgress = 0;
    this._progress = 0;
    this._progressWhenDone = 0;
    this._progressStatus = "idle";
    this._pauseReason = undefined;
  }
}
